import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useAuth } from "../auth/AuthContext";
import { relativeTimeLabel } from "../lib/relativeTime";
import { MainCard } from "../components/MainCard";

const DEFAULT_DAYS = 30;

/**
 * Analytics tab (docs/357-ADMIN-ANALYTICS-AND-ACCESS-TRACKING.md v0) — "how each admin
 * uses the admin site" (page-access volume, most-used pages, active admins, and geo)
 * alongside the built docs/310 Usage tab ("how each admin's PROVIDER content is used")
 * and the built docs/340 Audit page ("who did what") — three distinct, non-overlapping
 * surfaces (docs/357 §2's own table).
 *
 * `GET /admin/access/stats` already returns per-provider data only (this deployment's
 * OWN `admin_access_log` rows — one Worker deployment = one provider, docs/300/310's
 * own precedent) — nothing here is ever cross-tenant; cross-tenant aggregation is the
 * fleet console's job (docs/354), not this page's.
 */
export function AnalyticsPage() {
  const { client, token } = useAuth();
  const [daysInput, setDaysInput] = useState(String(DEFAULT_DAYS));
  const [days, setDays] = useState<number | undefined>(DEFAULT_DAYS);

  const statsQuery = useQuery({
    queryKey: ["admin-access-stats", days],
    queryFn: () => client!.adminAccessStats(token!, { days }),
    enabled: !!client && !!token,
  });
  const stats = statsQuery.data;

  const refresh = () => {
    const parsed = Number(daysInput);
    setDays(Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : undefined);
  };

  return (
    <>
      <MainCard
        title="Analytics"
        sx={{ mb: 2 }}
        secondary={
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
            <TextField
              type="number"
              size="small"
              label="Window (days)"
              sx={{ maxWidth: 160 }}
              value={daysInput}
              onChange={(e) => setDaysInput(e.target.value)}
            />
            <Button size="small" variant="outlined" color="secondary" onClick={refresh}>
              Refresh
            </Button>
          </Box>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
          Which admin pages were opened, by whom, and from where — a client-emitted
          beacon, off entirely when "Track admin page views" is unchecked on Provider
          Settings. Admin ACTIONS (deletes, resets, grants, …) are on the separate{" "}
          <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
            Audit
          </Typography>{" "}
          page.
        </Typography>
      </MainCard>

      {statsQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(statsQuery.error as Error).message}
        </Alert>
      ) : statsQuery.isLoading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : !stats || stats.totalViews === 0 ? (
        <Typography color="text.secondary">
          No page views recorded yet in this window — either nobody's opened the admin
          site recently, or tracking is off (Provider Settings).
        </Typography>
      ) : (
        <>
          <MainCard sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Total page views
            </Typography>
            <Typography variant="h4" sx={{ mb: 0.5 }}>
              {stats.totalViews}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
              Active admins: {stats.activeAdmins}
            </Typography>
          </MainCard>

          <MainCard title="Most-viewed pages" sx={{ mb: 2 }}>
            <Stack spacing={1}>
              {stats.byPath.map((row) => (
                <Box key={row.path} sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontFamily: "monospace" }}>{row.path}</Typography>
                  <Typography color="text.secondary">{row.count}</Typography>
                </Box>
              ))}
            </Stack>
          </MainCard>

          <MainCard title="By admin" sx={{ mb: 2 }}>
            <Stack spacing={1}>
              {stats.byAdmin.map((row) => (
                <Box key={row.actorId} sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontFamily: "monospace" }}>
                    {row.actorId === "admin-key" ? "admin key" : row.actorId}
                  </Typography>
                  <Typography color="text.secondary">{row.count}</Typography>
                </Box>
              ))}
            </Stack>
          </MainCard>

          <MainCard title="By country" sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Small buckets are folded into "Other" to avoid singling out one admin's own
              location.
            </Typography>
            <Stack spacing={1}>
              {stats.byCountry.map((row) => (
                <Box key={row.country ?? "other"} sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>{row.country ?? "Other / unknown"}</Typography>
                  <Typography color="text.secondary">{row.count}</Typography>
                </Box>
              ))}
            </Stack>
          </MainCard>

          <MainCard title="Recent activity">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Admin</TableCell>
                    <TableCell>Page</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>When</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.recent.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {row.actorId === "admin-key" ? "admin key" : row.actorId}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>{row.path}</TableCell>
                      <TableCell>{row.country ?? "—"}</TableCell>
                      <TableCell>
                        <Tooltip title={new Date(row.at * 1000).toLocaleString()}>
                          <span>{relativeTimeLabel(Date.now(), row.at * 1000)}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MainCard>
        </>
      )}
    </>
  );
}
